import useLocalStorage, {
  getStorage,
  LocalStorageMeta,
  LocalStorageOptions,
  persistStorageValue,
  readStorageValue
} from "@pathwright/utils/src/storage/useLocalStorage"
import { safeParseAuthToken } from "@pathwright/utils/src/token/utils"
import { useEffect, useMemo } from "react"
import isEqual from "react-fast-compare"

type BaseFormStateKey = string | number | null | undefined

export type FormStateKey =
  | BaseFormStateKey
  | Record<string, BaseFormStateKey>
  | (BaseFormStateKey | Record<string, BaseFormStateKey>)[]

export type FormStateStorage<T = any> =
  | {
      values: T | null | undefined
      initialValues: T | null | undefined
    }
  | null
  | undefined

export type FormStateStorageOptions = {
  key?: FormStateKey
  formStateMigrationKey?: string
  localStorageOptions?: LocalStorageOptions
}

type FormStateStorageSetter<T = any> = (value?: T | null) => void

type FormStateStorageReturn<T = any> = [
  FormStateStorage<T>,
  FormStateStorageSetter<T>,
  LocalStorageMeta<FormStateStorage<T>>
]

export const getFormStateStorageKey = (value?: FormStateKey) => {
  const objReducer = (value?: FormStateKey) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return Object.entries(value).reduce((acc, [k, v]) => {
        if (v) {
          acc.push(k, `${v}`)
        }
        return acc
      }, [] as string[])
    }
    return value
  }

  value = objReducer(value)

  // Convert number to string.
  if (typeof value === "number") {
    value = `${value}`
  }

  // Convert string to array.
  if (typeof value === "string") {
    value = [value]
  }

  // Defaulting to location.pathname.
  if (!value) {
    value = window.location.pathname.split("/")
  }

  // In case we have an array containing flat objects, lets reduce those.
  value = value.reduce(
    // @ts-ignore - Type 'undefined' is not assignable to type 'ConcatArray<never>'
    (acc, part) => (part ? [...acc, ...[].concat(objReducer(part))] : acc),
    []
  )

  // Supply the user ID if to store form state for a specific user.
  const keys = [...getUserIdKey(), ...value].filter(Boolean).map(String)

  // Only insert "formState" when no segment already contains it.
  if (!keys.some((part) => part.includes("formState"))) {
    keys.unshift("formState")
  }

  return keys.join(":")
}

// Next stored form state for incoming values. If no values are provided, the
// intent is to discard storage: `null` retains the key but clears its data,
// `undefined` removes the key. Otherwise update values and ensure an
// initialValues is set.
export function nextStoredFormState<T = any>(
  current: FormStateStorage<T>,
  values: T | null | undefined,
  fallbackInitialValues: T
): FormStateStorage<T> {
  if (!values) return values === null ? null : undefined

  // Default to preserving initialValues. If there are not yet any
  // stored initialValues, then set them.
  const nextInitialValues = current?.initialValues || fallbackInitialValues

  // If the incoming values match what we would store as initialValues,
  // discard instead of writing an unmodified snapshot.
  if (isEqual(values, nextInitialValues)) return undefined

  return {
    values,
    initialValues: nextInitialValues
  }
}

// Read/write stored form state without a mounted form, e.g. when a submit
// settles after the form has unmounted. `storageKey` is the resolved key
// from getFormStateStorageKey.
export function readStoredFormState<T = any>(
  storageKey: string,
  localStorageOptions?: LocalStorageOptions
): FormStateStorage<T> {
  return readStorageValue<FormStateStorage<T>>(
    getStorage(localStorageOptions?.storageType),
    storageKey
  )
}

export function writeStoredFormState<T = any>(
  storageKey: string,
  values: T | null | undefined,
  fallbackInitialValues: T,
  localStorageOptions?: LocalStorageOptions
) {
  const storage = getStorage(localStorageOptions?.storageType)
  const current = readStorageValue<FormStateStorage<T>>(storage, storageKey)
  persistStorageValue(
    storage,
    storageKey,
    nextStoredFormState(current, values, fallbackInitialValues)
  )
}

// Merge a patch into a form's stored draft while that form is unmounted, so
// it picks the change up when it mounts again (e.g. a subpanel handing a
// result back to its parent panel's form). Starts from `fallbackInitialValues`
// when there is no stored draft yet.
export function mergeStoredFormValues<T extends object = any>(
  formStateKey: FormStateKey,
  patch: Partial<T>,
  fallbackInitialValues: T,
  localStorageOptions?: LocalStorageOptions
) {
  const storageKey = getFormStateStorageKey(formStateKey)
  const current = readStoredFormState<T>(storageKey, localStorageOptions)
  writeStoredFormState<T>(
    storageKey,
    { ...(current?.values || fallbackInitialValues), ...patch },
    fallbackInitialValues,
    localStorageOptions
  )
}

// Get userId from auth token for form state key.
function getUserIdKey() {
  const token = localStorage.getItem("auth_token")
  const data = token ? safeParseAuthToken(token) : null
  const userId = data && data.success ? data.data.sub : null
  return userId ? ["user", userId] : []
}

const useFormStateStorage = <T = any>(
  initialValues: T,
  {
    key,
    formStateMigrationKey,
    localStorageOptions
  }: FormStateStorageOptions = {}
): FormStateStorageReturn<T> => {
  // Using the window.location.pathname to key the form state ensures uniqueness.
  const formStateStorageKey = getFormStateStorageKey(key)

  // Prefer an explicit migration key when the current key is empty, otherwise
  // locate wildcard keys that match the current key and migrate those.
  const formStateStorageMigrateKey = useMemo(() => {
    // If the current key has a value, then keep that value.
    if (localStorage.getItem(formStateStorageKey)) return

    if (
      formStateMigrationKey &&
      formStateMigrationKey !== formStateStorageKey &&
      localStorage.getItem(formStateMigrationKey)
    ) {
      return formStateMigrationKey
    }

    let baseKey = getFormStateStorageKey([])
    const wildCardKeys = Object.keys(localStorage).filter(
      (key) => key.startsWith(baseKey) && key.includes("*")
    )

    // If there are no wild card keys, then there is nothing to do.
    if (!wildCardKeys.length) return

    const currentKeyParts = formStateStorageKey.split(":")

    // If a wild card key's parts match the current key's parts (or is a wildcard), then use that key.
    const key = wildCardKeys.find((key) => {
      const keyParts = key.split(":")
      return keyParts.every(
        (part, index) => part === currentKeyParts[index] || part === "*"
      )
    })

    return key
  }, [formStateStorageKey, formStateMigrationKey])

  // Whether the formStateStorage.initialValues has diverged from the initialValues.
  const getInitialValueDiverged = (formStateStorage?: FormStateStorage<T>) => {
    return Boolean(
      formStateStorage?.initialValues &&
        !isEqual(initialValues, formStateStorage.initialValues)
    )
  }

  const getValuesUnmodified = (formStateStorage?: FormStateStorage<T>) => {
    return Boolean(
      formStateStorage &&
        isEqual(formStateStorage.initialValues, formStateStorage.values)
    )
  }

  function discardUnchangedFormState(formStateStorage?: FormStateStorage<T>) {
    if (
      getInitialValueDiverged(formStateStorage) ||
      getValuesUnmodified(formStateStorage)
    ) {
      // Discard changes.
      setFormStorageState()
    }
  }

  const getInitialValue = (formStateStorage?: FormStateStorage<T>) => {
    if (getInitialValueDiverged(formStateStorage)) {
      if (formStateStorage) {
        // We need to default to initialValues. This way, we ensure the user doesn't
        // unknowingly overwrite data.
        formStateStorage.values = initialValues
        formStateStorage.initialValues = initialValues
      }
    }

    // Return current formStateStorage, possibly modified above.
    if (formStateStorage !== undefined) return formStateStorage

    // Return the default formStateStorage
    return {
      // Track current formStateStorage.
      values: initialValues,
      // Track formStateStorage.initialValues which can be compared to initialValues to determine
      // if the initialValues have changed since last form mount. If changed, we can
      // assume data has been altered in the DB and the safest path forward is to
      // discard local formStateStorage.
      initialValues: null
    }
  }

  const [formStateStorage, setFormStorageState, formStateStorageMeta] =
    useLocalStorage<FormStateStorage<T>>(formStateStorageKey, getInitialValue, {
      ...localStorageOptions,
      migrateKey: formStateStorageMigrateKey
    })

  // Discard when stored values match stored initialValues (or when the form's
  // initialValues have diverged from storage). Must re-run whenever storage
  // changes — not only on mount — so resetForm/clear-to-initial after typing
  // actually removes the key instead of leaving an unmodified snapshot.
  useEffect(() => {
    discardUnchangedFormState(formStateStorage)
  }, [formStateStorage])

  // Use a ref to the latest formStateStorage in effects
  // unmounted closures to ensure we have latest value.
  useEffect(() => {
    return () => {
      discardUnchangedFormState(formStateStorageMeta.valueRef.current)
    }
  }, [])

  // Wraps setFormStorageState. Values should be an object, null, or undefined.
  // Update via function to ensure we're using the latest formStateStorage.
  const setFormStateWrapper: FormStateStorageSetter<T> = (values) => {
    setFormStorageState((formStateStorage) =>
      nextStoredFormState(formStateStorage, values, initialValues)
    )
  }

  return [formStateStorage, setFormStateWrapper, formStateStorageMeta]
}

export default useFormStateStorage
